import { useCallback, useEffect, useMemo, useState } from "react";
import PlanEditor from "@/pages/PlanEditor";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, Camera, CheckCircle2, ClipboardCheck, FileText, HelpCircle,
  Loader2, Plus, ShieldCheck, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { apiRequest } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const SEV = {
  blocker: "bg-rose-200 text-rose-900 border-rose-400",
  critical: "bg-rose-100 text-rose-800 border-rose-300",
  major: "bg-orange-100 text-orange-800 border-orange-300",
  minor: "bg-amber-100 text-amber-800 border-amber-300",
  info: "bg-blue-100 text-blue-800 border-blue-300",
};
const STATUS_LABEL = {
  survey_draft: "In compilazione", survey_complete: "Sopralluogo completo", risk_analysis: "Analisi rischi",
  auto_review_1: "Revisione 1 fatta", auto_review_2: "Revisione 2 fatta", auto_review_3: "Revisione 3 fatta",
  human_review: "In revisione umana", changes_requested: "Modifiche richieste", approved: "Approvato",
};
// dati anagrafici richiesti dai documenti oltre a azienda e sede (chiavi lette dal composer)
const META_FIELDS = {
  duvri: [["committente", "Committente"], ["referente_committente", "Referente del committente"], ["contratto", "Contratto / appalto"], ["durata", "Durata"], ["appaltatori", "Imprese appaltatrici (separate da virgola)"], ["luoghi_condivisi", "Luoghi condivisi"]],
  pimus: [["cantiere", "Cantiere"], ["impresa", "Impresa"], ["ponteggio", "Ponteggio (tipo e marca)"], ["preposto", "Preposto"], ["responsabile_cantiere", "Responsabile di cantiere"], ["data_montaggio", "Data prevista montaggio"]],
  emergenza: [["attivita", "Attività svolta"], ["coordinatore_emergenza", "Coordinatore dell'emergenza"]],
  pos: [["committente", "Committente"], ["responsabile_cantiere", "Responsabile di cantiere"]],
};
const HUMAN_ROLES = ["SUPER_ADMIN", "CONSULENTE", "RSPP"];
const eqName = (code) => (code || "").replace(/_/g, " ");

const LIST_META = ["appaltatori", "luoghi_condivisi"];
function cleanMeta(meta) {
  const out = {};
  Object.entries(meta).forEach(([k, v]) => {
    if (typeof v !== "string" || !v.trim()) return;
    out[k] = LIST_META.includes(k) ? v.split(",").map((x) => x.trim()).filter(Boolean) : v.trim();
  });
  return out;
}

function SeverityBadge({ s }) {
  return <Badge variant="outline" className={SEV[s] || SEV.info}>{s}</Badge>;
}

export default function SafetySurvey() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const base = `/companies/${id}/safety`;
  const [catalog, setCatalog] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [current, setCurrent] = useState(null); // {survey, evaluation, review_state, exceptions}
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([apiRequest(`${base}/catalog`), apiRequest(`${base}/surveys`)]);
      setCatalog(c); setSurveys(l);
    } catch (e) { toast.error("Errore", { description: e.message }); }
  }, [base]);
  useEffect(() => { loadList(); }, [loadList]);
  const [params] = useSearchParams();
  const wanted = params.get("survey");  // arrivo dalla casella del consulente
  useEffect(() => { if (wanted) apiRequest(`${base}/surveys/${wanted}`).then(setCurrent).catch(() => null); }, [wanted, base]);

  const open = async (sid) => setCurrent(await apiRequest(`${base}/surveys/${sid}`));
  const refresh = async () => current && open(current.survey.id);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) {
      const d = e.message;
      toast.error("Operazione non riuscita", { description: typeof d === "string" ? d : JSON.stringify(d) });
    } finally { setBusy(false); }
  };

  if (!catalog) return <div className="p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <div className="space-y-6" data-testid="safety-survey-page">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => (current ? (setCurrent(null), loadList()) : navigate(`/aziende/${id}`))}>
          <ArrowLeft className="h-4 w-4" /> {current ? "Elenco sopralluoghi" : "Scheda azienda"}
        </Button>
        <Badge variant="outline">Base normativa {catalog.kb_version}</Badge>
      </div>
      <h2 className="font-heading text-4xl font-bold text-slate-950">Sopralluogo guidato</h2>
      <Alert>
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Tu raccogli i dati, l'app valuta</AlertTitle>
        <AlertDescription>
          Rispondi alle domande e allega le foto richieste. I rischi, i documenti da chiedere al cliente e le
          valutazioni specialistiche vengono individuati dall'app. Il documento resta una bozza finché un
          revisore abilitato non lo approva.
          {catalog.kb_unverified_provisions.length > 0 &&
            ` ${catalog.kb_unverified_provisions.length} riferimenti normativi sono ancora da validare.`}
        </AlertDescription>
      </Alert>
      {!current
        ? <SurveyList catalog={catalog} surveys={surveys} base={base} busy={busy} act={act} open={open} />
        : <SurveyWorkspace data={current} base={base} user={user} busy={busy} act={act} refresh={refresh} setCurrent={setCurrent} />}
    </div>
  );
}

// ------------------------------------------------------------------ elenco / nuovo
const CLASS_STYLE = { basso: "bg-emerald-100 text-emerald-800 border-emerald-300", medio: "bg-amber-100 text-amber-800 border-amber-300", alto: "bg-rose-100 text-rose-800 border-rose-300" };

// codice ATECO -> classe di rischio indicativa + lavorazioni tipiche (dalla tabella ufficiale caricata sul server)
export function useAteco(base, code) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const c = (code || "").trim();
    if (c.replace(/\D/g, "").length < 2) { setInfo(null); return undefined; }
    const h = setTimeout(() => { apiRequest(`${base}/ateco/${encodeURIComponent(c)}`).then(setInfo).catch(() => setInfo(null)); }, 400);
    return () => clearTimeout(h);
  }, [base, code]);
  return info;
}

export function AtecoBox({ info }) {
  if (!info) return null;
  if (!info.valid) return <p className="text-sm text-rose-700">{info.message}</p>;
  return (
    <div className="space-y-1 rounded border bg-slate-50 p-3 text-sm" data-testid="ateco-box">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">ATECO {info.edition === "2025" ? "2025 " : info.edition === "2022" ? "2007/2022 " : ""}{info.code}</span>
        {info.risk_class
          ? <Badge variant="outline" className={CLASS_STYLE[info.risk_class]}>Classe di rischio {info.risk_class.toUpperCase()}</Badge>
          : <Badge variant="outline">Classe da stabilire</Badge>}
        {info.macro_sector && <span className="text-slate-600">{info.macro_sector}</span>}
      </div>
      {info.title && <p className="text-slate-700">{info.title}</p>}
      {info.edition === "2025" && info.codes_2007?.length > 0 && <p className="text-xs text-slate-500">Raccordo ISTAT con ATECO 2007: {info.codes_2007.join(", ")}</p>}
      {info.ambiguous && <p className="text-xs font-semibold text-amber-700">Il codice ha più corrispondenze con classi diverse: è proposta la classe più alta, da confermare.</p>}
      {info.description && <p className="text-slate-600">{info.description}</p>}
      <p className="text-xs text-slate-500">{info.message}{info.source?.act ? ` Fonte: ${info.source.act}.` : ""}</p>
      {info.ateco_2025_note && <p className="text-xs text-slate-500">{info.ateco_2025_note}</p>}
    </div>
  );
}

const EMPTY_COMPANY = { business_name: "", vat_number: "", fiscal_code: "", legal_address: "", operating_address: "", ateco_code: "", employees_total: "", workers: [] };

export function CompanyFields({ base, company, setCompany }) {
  const info = useAteco(base, company.ateco_code);
  const set = (k) => (e) => setCompany({ ...company, [k]: e.target.value });
  return (
    <div className="space-y-3" data-testid="company-fields">
      <Input placeholder="Ragione sociale" value={company.business_name} onChange={set("business_name")} />
      <div className="grid gap-2 sm:grid-cols-2">
        <Input placeholder="Partita IVA" value={company.vat_number} onChange={set("vat_number")} inputMode="numeric" />
        <Input placeholder="Codice fiscale" value={company.fiscal_code} onChange={set("fiscal_code")} />
      </div>
      <Input placeholder="Sede legale" value={company.legal_address} onChange={set("legal_address")} />
      <Input placeholder="Sede operativa / cantiere (dove fai il sopralluogo)" value={company.operating_address} onChange={set("operating_address")} />
      <div className="grid gap-2 sm:grid-cols-2">
        <Input placeholder="Codice ATECO (es. 43.21.01)" value={company.ateco_code} onChange={set("ateco_code")} />
        <Input placeholder="Numero di dipendenti" value={company.employees_total} onChange={set("employees_total")} inputMode="numeric" />
      </div>
      <AtecoBox info={info} />
    </div>
  );
}

function WorkersList({ workers, setWorkers }) {
  const upd = (i, k, v) => setWorkers(workers.map((w, j) => (j === i ? { ...w, [k]: v } : w)));
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">Elenco di chi lavora qui: solo nome e mansione. Codici fiscali e dati sanitari non si raccolgono in sopralluogo.</p>
      {workers.map((w, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Nome e cognome" value={w.full_name} onChange={(e) => upd(i, "full_name", e.target.value)} />
          <Input placeholder="Mansione" value={w.job_title} onChange={(e) => upd(i, "job_title", e.target.value)} />
          <Button size="sm" variant="ghost" onClick={() => setWorkers(workers.filter((_, j) => j !== i))}>×</Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setWorkers([...workers, { full_name: "", job_title: "" }])}><Plus className="h-4 w-4" /> Aggiungi lavoratore</Button>
    </div>
  );
}

function PlansTab({ base, survey, act }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const pBase = `${base}/surveys/${survey.id}/plans`;
  const load = useCallback(() => apiRequest(pBase).then(setList).catch(() => setList([])), [pBase]);
  useEffect(() => { load(); }, [load]);
  const kinds = survey.document_kinds || [survey.document_kind];
  const offered = [["evacuazione", "Piano di evacuazione", kinds.includes("emergenza")], ["pimus_pianta", "PiMUS: pianta del ponteggio", kinds.includes("pimus")], ["pimus_prospetto", "PiMUS: prospetto del ponteggio", kinds.includes("pimus")], ["planimetria", "Planimetria generica", true]];
  const create = (kind, label) => act(async () => { const r = await apiRequest(pBase, { method: "POST", body: JSON.stringify({ title: label, kind }) }); setOpen(r.id); });
  if (open) return <PlanEditor base={base} surveyId={survey.id} planId={open} onBack={() => { setOpen(null); load(); }} />;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card><CardHeader><CardTitle>Nuova planimetria</CardTitle></CardHeader><CardContent className="space-y-2">
        <p className="text-sm text-slate-600">Parti da una foto di una planimetria catastale o di un vecchio piano di evacuazione, segnala le modifiche e ottieni il PDF nello standard Artego.</p>
        {offered.map(([k, l, rec]) => <Button key={k} variant={rec ? "default" : "outline"} className="mr-2" onClick={() => create(k, l)}><Plus className="h-4 w-4" /> {l}</Button>)}
      </CardContent></Card>
      <Card><CardHeader><CardTitle>Planimetrie del sopralluogo</CardTitle></CardHeader><CardContent className="space-y-2">
        {!list && <Loader2 className="h-4 w-4 animate-spin" />}
        {list && list.length === 0 && <p className="text-sm text-slate-500">Nessuna planimetria.</p>}
        {(list || []).map((p) => (
          <button key={p.id} onClick={() => setOpen(p.id)} className="flex w-full items-center justify-between rounded border p-3 text-left hover:bg-slate-50">
            <span>{p.title}<span className="block text-xs text-slate-500">{p.kind}</span></span>
            <Badge variant="outline" className={p.has_pdf ? "bg-emerald-100 text-emerald-800" : ""}>{p.has_pdf ? "PDF allegato" : p.status}</Badge>
          </button>))}
      </CardContent></Card>
    </div>
  );
}

function KindsTab({ data, base, sBase, act, refresh }) {
  const { survey } = data;
  const [catalog, setCatalog] = useState(null);
  const [meta, setMeta] = useState({});
  useEffect(() => { apiRequest(`${base}/catalog`).then(setCatalog).catch(() => null); }, [base]);
  const locked = survey.status === "approved";
  const kinds = survey.document_kinds || [survey.document_kind];
  const ready = data.document_readiness || {};
  const save = (list) => act(async () => { await apiRequest(`${sBase}/kinds`, { method: "PUT", body: JSON.stringify({ document_kinds: list }) }); refresh(); });
  const saveMeta = () => act(async () => { await apiRequest(`${sBase}/meta`, { method: "PUT", body: JSON.stringify({ meta: cleanMeta(meta) }) }); toast.success("Dati salvati"); refresh(); });
  if (!catalog) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card><CardHeader><CardTitle>Documenti da generare</CardTitle></CardHeader><CardContent className="space-y-3">
        <p className="text-sm text-slate-600">Cambiarli aggiunge o toglie le domande necessarie.</p>
        <KindPicker catalog={catalog} kinds={kinds} setKinds={(l) => (!locked && l.length ? save(l) : null)} />
      </CardContent></Card>
      <Card><CardHeader><CardTitle>Sono pronti?</CardTitle></CardHeader><CardContent className="space-y-3">
        {kinds.map((k) => { const r = ready[k]; if (!r) return null; return (
          <div key={k} className="rounded border p-3">
            <div className="flex items-center justify-between"><b>{r.icon} {r.label}</b><Badge variant="outline" className={r.ready ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{r.ready ? "dati completi" : "da completare"}</Badge></div>
            {r.missing.map((m) => m.type === "meta"
              ? <Input key={m.key} className="mt-2" placeholder={m.label} value={meta[m.key] || ""} onChange={(e) => setMeta({ ...meta, [m.key]: e.target.value })} />
              : <p key={m.key} className="mt-1 text-xs text-rose-700">Manca: {m.label}{m.type === "company" ? " (scheda Azienda)" : m.type === "plan" ? " (scheda Planimetrie)" : " (lavorazione da aggiungere)"}</p>)}
          </div>); })}
        <Button disabled={locked || !Object.keys(meta).length} onClick={saveMeta}>Salva i dati</Button>
      </CardContent></Card>
    </div>
  );
}

function CompanyTab({ data, sBase, act, refresh, base }) {
  const { survey } = data;
  const locked = survey.status === "approved";
  const [company, setCompany] = useState({ ...EMPTY_COMPANY, ...(survey.company || {}) });
  const [sync, setSync] = useState(false);
  const save = () => act(async () => {
    await apiRequest(`${sBase}/company`, { method: "PUT", body: JSON.stringify({ ...company, employees_total: company.employees_total === "" ? null : company.employees_total, sync_master: sync }) });
    toast.success("Dati azienda salvati"); refresh();
  });
  const upload = (d, file) => act(async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "document");
    const e = await apiRequest(`${sBase}/evidence`, { method: "POST", body: fd });
    await apiRequest(`${sBase}/company-docs/${d.code}`, { method: "PUT", body: JSON.stringify({ status: "received", evidence_id: e.id }) });
    toast.success("Documento acquisito"); refresh();
  });
  const notApplicable = (d) => {
    const note = window.prompt("Perché non è applicabile? (scrivi la motivazione)");
    if (note) act(async () => { await apiRequest(`${sBase}/company-docs/${d.code}`, { method: "PUT", body: JSON.stringify({ status: "not_applicable", note }) }); refresh(); });
  };
  const docs = data.company_docs || [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card><CardHeader><CardTitle>Dati dell'azienda</CardTitle></CardHeader><CardContent className="space-y-4">
        <CompanyFields base={base} company={company} setCompany={setCompany} />
        <WorkersList workers={company.workers || []} setWorkers={(w) => setCompany({ ...company, workers: w })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sync} onChange={(e) => setSync(e.target.checked)} /> Aggiorna anche la scheda azienda (ATECO, dipendenti, classe di rischio)</label>
        <Button disabled={locked} onClick={save} data-testid="company-save">Salva i dati dell'azienda</Button>
      </CardContent></Card>
      <Card><CardHeader><CardTitle>Documenti da chiedere subito ({docs.filter((d) => d.status !== "received").length} mancanti)</CardTitle></CardHeader><CardContent className="space-y-2">
        <p className="text-xs text-slate-500">Fotografali o caricali dal telefono. Il revisore decide quali servono davvero.</p>
        {docs.map((d) => (
          <div key={d.code} className="flex items-center justify-between gap-2 rounded border p-3">
            <div className="min-w-0"><p className="text-sm font-medium">{d.name}</p><p className="text-xs text-slate-500">{d.why}{d.note ? ` · ${d.note}` : ""}</p></div>
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant="outline" className={d.status === "received" ? "bg-emerald-100 text-emerald-800" : ""}>{d.status === "received" ? "ricevuto" : d.status === "not_applicable" ? "non applicabile" : "da chiedere"}</Badge>
              {!locked && d.status !== "received" && <>
                <label className="cursor-pointer"><span className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs"><Camera className="h-3 w-3" /> Foto / file</span>
                  <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && upload(d, e.target.files[0])} /></label>
                <button className="text-xs underline" onClick={() => notApplicable(d)}>N/A</button></>}
            </div>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}


// ------------------------------------------------ attività: albero ATECO con icone
export function AtecoTree({ base, catalog, tasks, setTasks, atecoCode }) {
  const atecoInfo = useAteco(base, atecoCode);
  const [sec, setSec] = useState(null);
  const [div, setDiv] = useState(null);
  const [q, setQ] = useState("");
  const [openProc, setOpenProc] = useState({});
  const L = useMemo(() => {
    const phaseOf = Object.fromEntries(catalog.phases.map((p) => [p.code, p]));
    const procTasks = {};
    catalog.tasks.forEach((t) => { const ph = phaseOf[t.phase_code]; if (ph) (procTasks[ph.process_code] = procTasks[ph.process_code] || []).push({ ...t, seq: ph.sequence }); });
    Object.values(procTasks).forEach((a) => a.sort((x, y) => x.seq - y.seq || x.code.localeCompare(y.code)));
    const procByAct = {};
    catalog.processes.forEach((p) => { (procByAct[p.activity_code] = procByAct[p.activity_code] || []).push(p); });
    const act = Object.fromEntries(catalog.activities.map((a) => [a.code, a]));
    const divisions = catalog.ateco_tree.flatMap((s) => s.divisions.map((d) => ({ sec: s, d })));
    const procOf = Object.fromEntries(catalog.processes.map((p) => [p.code, p]));
    return { procTasks, procByAct, act, divisions, phaseOf, procOf };
  }, [catalog]);
  const ql = q.trim().toLowerCase();
  // le divisioni dell'albero sono ATECO 2007: per un codice ATECO 2025 si usano quelle ricavate dal raccordo ISTAT (server)
  const divs07 = atecoInfo?.valid ? (atecoInfo.divisions_2007 || []) : [];
  const rec = divs07.length ? L.divisions.find((x) => divs07.includes(x.d.prefix.slice(0, 2))) : null;
  const toggle = (c) => setTasks(tasks.includes(c) ? tasks.filter((t) => t !== c) : [...tasks, c]);
  const selectAll = (pc) => {
    const ts = (L.procTasks[pc] || []).map((t) => t.code); const all = ts.every((t) => tasks.includes(t));
    setTasks(all ? tasks.filter((t) => !ts.includes(t)) : Array.from(new Set([...tasks, ...ts])));
  };
  const TaskRow = ({ t, withProc }) => (
    <button type="button" onClick={() => toggle(t.code)} className={`flex w-full items-center gap-3 rounded border-2 p-3 text-left ${tasks.includes(t.code) ? "border-teal-800 bg-teal-50" : "border-slate-200 bg-white"}`}>
      <span className={`grid h-6 w-6 place-items-center rounded border-2 text-xs font-bold ${tasks.includes(t.code) ? "border-teal-800 bg-teal-800 text-white" : "border-slate-400"}`}>{tasks.includes(t.code) ? "✓" : ""}</span>
      <span className="text-sm">{t.name}{withProc && <span className="block text-xs text-slate-500">{L.procOf[L.phaseOf[t.phase_code]?.process_code]?.name}</span>}</span>
    </button>
  );
  const DivRow = ({ x }) => (
    <button type="button" onClick={() => { setDiv(x.d.prefix); setQ(""); }} className="flex w-full items-center gap-3 rounded border-2 border-slate-200 bg-white p-3 text-left hover:border-teal-800">
      <span className="text-3xl">{x.d.icon}</span>
      <span className="min-w-0 flex-1"><b className="block text-sm">{x.d.name}</b><span className="text-xs text-slate-500">ATECO {x.d.prefix} · {x.sec.name}</span></span>
      {x.d.risk_class && <Badge variant="outline" className={CLASS_STYLE[x.d.risk_class]}>rischio {x.d.risk_class}</Badge>}
      <Badge variant="outline" className={x.d.coverage === "specific" ? "bg-teal-50" : ""}>{x.d.coverage === "specific" ? "checklist specifica" : "checklist generale"}</Badge>
    </button>
  );
  let body;
  if (div) {
    const x = L.divisions.find((y) => y.d.prefix === div);
    const acts = [...x.d.activities]; if (catalog.generic_activity && !acts.includes(catalog.generic_activity)) acts.push(catalog.generic_activity);
    body = (
      <div className="space-y-3">
        <Button size="sm" variant="ghost" onClick={() => setDiv(null)}>← Tutte le attività</Button>
        <h3 className="text-lg font-semibold">{x.d.icon} {x.d.name}</h3>
        {x.d.coverage !== "specific" && <p className="rounded bg-slate-50 p-2 text-sm">Per questa attività si usa la <b>checklist generale</b>: copre i rischi comuni a qualunque luogo di lavoro. Le domande specifiche del settore si aggiungono man mano.</p>}
        {acts.map((a) => (
          <div key={a} className="space-y-2">
            <p className="text-sm font-semibold">{a === catalog.generic_activity ? "🧰 Checklist generale, valida per qualsiasi attività" : `📋 ${L.act[a]?.name}`}</p>
            {(L.procByAct[a] || []).map((p) => {
              const ts = L.procTasks[p.code] || []; const n = ts.filter((t) => tasks.includes(t.code)).length;
              const open = openProc[p.code] ?? ts.length <= 4;
              return (
                <div key={p.code} className="rounded border bg-white">
                  <button type="button" className="flex w-full items-center justify-between gap-2 p-3 text-left font-medium" onClick={() => setOpenProc({ ...openProc, [p.code]: !open })}>
                    <span>{open ? "▾" : "▸"} {p.name}</span><Badge variant="outline">{n ? `${n} scelte` : `${ts.length} lavorazioni`}</Badge>
                  </button>
                  {open && <div className="space-y-2 border-t p-2">
                    <Button size="sm" variant="outline" onClick={() => selectAll(p.code)}>{n === ts.length ? "Deseleziona tutte" : "Seleziona tutte"}</Button>
                    {ts.map((t) => <TaskRow key={t.code} t={t} />)}
                  </div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  } else if (ql) {
    const hits = L.divisions.filter((x) => `${x.d.name} ${x.sec.name} ${x.d.prefix}`.toLowerCase().includes(ql));
    const tk = catalog.tasks.filter((t) => t.name.toLowerCase().includes(ql)).slice(0, 12);
    body = <div className="space-y-2"><p className="text-xs text-slate-500">{hits.length} attività trovate</p>{hits.map((x) => <DivRow key={x.d.prefix} x={x} />)}{tk.map((t) => <TaskRow key={t.code} t={t} withProc />)}{!hits.length && !tk.length && <p className="text-sm text-slate-500">Nessun risultato: prova un'altra parola.</p>}</div>;
  } else if (sec) {
    const s0 = catalog.ateco_tree.find((x) => x.letter === sec);
    body = <div className="space-y-2"><Button size="sm" variant="ghost" onClick={() => setSec(null)}>← Tutti i settori</Button><h3 className="text-lg font-semibold">{s0.icon} {s0.name}</h3>{s0.divisions.map((d) => <DivRow key={d.prefix} x={{ sec: s0, d }} />)}</div>;
  } else {
    body = (
      <div className="space-y-3">
        {rec && <button type="button" onClick={() => setDiv(rec.d.prefix)} className="flex w-full items-center gap-3 rounded border-2 border-teal-800 bg-teal-50 p-3 text-left"><span className="text-3xl">{rec.d.icon}</span><span><Badge variant="outline">Dal tuo codice ATECO</Badge><b className="block">{rec.d.name}</b><span className="text-xs text-slate-600">{rec.sec.name} · tocca per scegliere le lavorazioni</span></span></button>}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {catalog.ateco_tree.map((s0) => (
            <button type="button" key={s0.letter} onClick={() => setSec(s0.letter)} className="rounded border-2 border-slate-200 bg-white p-3 text-left hover:border-teal-800">
              <span className="text-3xl">{s0.icon}</span><b className="mt-1 block text-sm leading-tight">{s0.name}</b><span className="text-xs text-slate-500">{s0.divisions.length} attività</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="ateco-tree">
      <Input id="ateco-search" placeholder="🔎 Cerca: ristorante, falegname, scuola, trasporti..." value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-[32rem] overflow-y-auto pr-1">{body}</div>
      <p className="text-sm font-medium">{tasks.length} lavorazioni scelte</p>
    </div>
  );
}

// ------------------------------------------------ quali documenti generare
export function KindPicker({ catalog, kinds, setKinds }) {
  const toggle = (k) => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="kind-picker">
      {catalog.document_catalog.map((c) => (
        <button type="button" key={c.kind} onClick={() => toggle(c.kind)} className={`rounded border-2 p-3 text-left ${kinds.includes(c.kind) ? "border-teal-800 bg-teal-50" : "border-slate-200 bg-white"}`}>
          <div className="flex items-start justify-between"><span className="text-3xl">{c.icon}</span><span className={`grid h-6 w-6 place-items-center rounded border-2 text-xs font-bold ${kinds.includes(c.kind) ? "border-teal-800 bg-teal-800 text-white" : "border-slate-400"}`}>{kinds.includes(c.kind) ? "✓" : ""}</span></div>
          <b className="block">{c.label}</b><span className="block text-xs">{c.full}</span><span className="mt-1 block text-xs text-slate-500">{c.info}</span>
          {c.adds_tasks && <Badge variant="outline" className="mt-1">Aggiunge domande dedicate</Badge>}
        </button>
      ))}
    </div>
  );
}

function SurveyList({ catalog, surveys, base, busy, act, open }) {
  const [tasks, setTasks] = useState([]);
  const [kinds, setKinds] = useState(["dvr"]);
  const [meta, setMeta] = useState({});
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const extra = Array.from(new Set(kinds.flatMap((k) => (META_FIELDS[k] || []).map((f) => f[0]))));
  const create = () => act(async () => {
    const r = await apiRequest(`${base}/surveys`, { method: "POST", body: JSON.stringify({ task_codes: tasks, meta: cleanMeta(meta), document_kinds: kinds }) });
    const filled = Object.values({ ...company, workers: "" }).some((v) => String(v).trim() !== "") || (company.workers || []).length;
    if (filled) await apiRequest(`${base}/surveys/${r.survey.id}/company`, { method: "PUT", body: JSON.stringify({ ...company, employees_total: company.employees_total === "" ? null : company.employees_total }) });
    open(r.survey.id);
  });
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <Card><CardHeader><CardTitle>1. Azienda</CardTitle></CardHeader><CardContent><CompanyFields base={base} company={company} setCompany={setCompany} /></CardContent></Card>
        <Card><CardHeader><CardTitle>2. Che documenti ti servono?</CardTitle></CardHeader><CardContent className="space-y-3">
          <p className="text-sm text-slate-600">Puoi sceglierne più di uno: la stessa checklist li alimenta tutti.</p>
          <KindPicker catalog={catalog} kinds={kinds} setKinds={setKinds} />
          {extra.length > 0 && <div className="space-y-2"><p className="text-xs text-slate-500">Dati richiesti dai documenti scelti (si possono completare anche dopo):</p>
            {extra.map((k) => { const lbl = Object.values(META_FIELDS).flat().find((f) => f[0] === k)[1]; return <Input key={k} placeholder={lbl} value={meta[k] || ""} onChange={(e) => setMeta({ ...meta, [k]: e.target.value })} />; })}</div>}
        </CardContent></Card>
        <Card><CardHeader><CardTitle>3. Che attività svolge?</CardTitle></CardHeader><CardContent className="space-y-3">
          <AtecoTree base={base} catalog={catalog} tasks={tasks} setTasks={setTasks} atecoCode={company.ateco_code} />
          <Button disabled={busy || !tasks.length || !kinds.length} onClick={create} data-testid="safety-create-button"><Plus className="h-4 w-4" /> Inizia il sopralluogo</Button>
        </CardContent></Card>
      </div>
      <Card className="h-fit">
        <CardHeader><CardTitle>Sopralluoghi</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {surveys.length === 0 && <p className="text-sm text-slate-500">Nessun sopralluogo.</p>}
          {surveys.map((s) => (
            <button key={s.id} onClick={() => open(s.id)} className="flex w-full items-center justify-between rounded border p-3 text-left hover:bg-slate-50">
              <span>{s.meta?.site || s.meta?.company_name || s.id.slice(0, 8)} <span className="text-xs text-slate-500">({(s.document_kinds || [s.document_kind]).map((k) => k.toUpperCase()).join(" + ")}, {s.task_codes.length} lavorazioni)</span></span>
              <Badge variant="outline">{STATUS_LABEL[s.status] || s.status}</Badge>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- area di lavoro
function SurveyWorkspace({ data, base, user, busy, act, refresh, setCurrent }) {
  const { survey, evaluation: ev, exceptions } = data;
  const [tab, setTab] = useState(data.survey.company ? "domande" : "azienda");
  const [editing, setEditing] = useState(null); // risposta da rivedere (stessa scheda della domanda)
  const done = ev.next_questions.length === 0;
  const sBase = `${base}/surveys/${survey.id}`;
  const post = (path, body) => apiRequest(`${sBase}${path}`, { method: "POST", body: body ? JSON.stringify(body) : undefined });
  const taskName = (c) => c;
  const tabs = [["azienda", "Azienda"], ["documenti_scelti", "Documenti scelti"], ["planimetrie", "Planimetrie"], ["domande", "Domande"], ["risposte", `Risposte (${(data.answered || []).length})`], ["rischi", `Rischi (${ev.risk_instances.length})`],
    ["documenti", `Documenti (${ev.documents.length})`], ["revisioni", "Revisioni"],
    ["umana", "Revisione umana"], ["bozza", "Bozza documento"]];
  return (
    <div className="space-y-4">
      <Card><CardContent className="space-y-2 pt-6">
        <div className="flex items-center justify-between">
          <span className="font-medium">{survey.meta?.site || survey.meta?.company_name || "Sopralluogo"} · {survey.document_kind.toUpperCase()}</span>
          <Badge variant="outline">{STATUS_LABEL[survey.status] || survey.status}</Badge>
        </div>
        <Progress value={ev.progress.percent} />
        <p className="text-xs text-slate-500">{ev.progress.percent}% completato · {ev.open_items.length} dati non disponibili · {ev.blockers.length} blocchi</p>
      </CardContent></Card>
      <SendCard data={data} done={done} busy={busy} act={act} post={post} refresh={refresh} setTab={setTab} />
      {["SUPER_ADMIN", "CONSULENTE"].includes(user?.role) && <AssignCard survey={survey} sBase={sBase} act={act} refresh={refresh} />}
      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, l]) => <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)}>{l}</Button>)}
      </div>
      {tab === "planimetrie" && <PlansTab base={base} survey={survey} act={act} />}
      {tab === "documenti_scelti" && <KindsTab data={data} base={base} sBase={sBase} act={act} refresh={refresh} />}
      {tab === "azienda" && <CompanyTab data={data} sBase={sBase} act={act} refresh={refresh} base={base} />}
      {tab === "risposte" && (editing
        ? <div className="space-y-2">
            <p className="text-xs text-amber-700">Stai correggendo una risposta: le revisioni già fatte diventeranno obsolete.</p>
            <QuestionCard q={editing} total={1} sBase={sBase} busy={busy} act={act} refresh={() => { setEditing(null); refresh(); }} taskName={taskName} />
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Annulla</Button>
          </div>
        : <div className="space-y-2">
            {(data.answered || []).length === 0 && <p className="text-sm text-slate-500">Nessuna risposta data.</p>}
            {(data.answered || []).map((a) => (
              <Card key={a.key}><CardContent className="flex items-center justify-between gap-3 pt-4 text-sm">
                <div>
                  <p className="font-medium">{a.text}</p>
                  <p className="text-xs text-slate-500">{a.task_code || "Generale"}{a.equipment_code ? ` · ${eqName(a.equipment_code)}` : ""} · {a.state === "answered" ? (Array.isArray(a.value) ? a.value.join(", ") : String(a.value)) : a.state}</p>
                </div>
                {survey.status !== "approved" && <Button size="sm" variant="outline" onClick={() => setEditing(a)}>Modifica</Button>}
              </CardContent></Card>
            ))}
          </div>)}
      {tab === "domande" && (done
        ? <Card><CardContent className="space-y-3 pt-6">
            <p className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Hai risposto a tutte le domande previste.</p>
            {survey.status === "survey_draft" && <Button disabled={busy} onClick={() => act(async () => { await post("/evaluate"); refresh(); })}>Chiudi il sopralluogo e analizza i rischi</Button>}
          </CardContent></Card>
        : <QuestionCard q={ev.next_questions[0]} total={ev.next_questions.length} sBase={sBase} busy={busy} act={act} refresh={refresh} taskName={taskName} />)}
      {tab === "rischi" && <Risks ev={ev} />}
      {tab === "documenti" && <Docs ev={ev} sBase={sBase} act={act} refresh={refresh} locked={survey.status === "approved"} units={survey.equipment_units || {}} />}
      {tab === "revisioni" && <Reviews data={data} post={post} act={act} refresh={refresh} busy={busy} />}
      {tab === "umana" && <HumanGate data={data} post={post} act={act} refresh={refresh} user={user} exceptions={exceptions} />}
      {tab === "bozza" && <Draft sBase={sBase} survey={survey} />}
    </div>
  );
}

// Il consulente puo' affidare un sopralluogo gia' aperto a uno o piu' auditor (di norma lo aprono loro, sul posto): lo compilano dal tablet, poi l'app elabora
// da sola e il documento arriva qui per la sola revisione.
function AssignCard({ survey, sBase, act, refresh }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(survey.assigned_to || []);
  useEffect(() => { apiRequest("/safety/auditors").then((r) => setList(r.auditors.filter((u) => u.is_active))).catch(() => setList([])); }, []);
  useEffect(() => { setSel(survey.assigned_to || []); }, [survey.assigned_to]);
  const locked = ["approved", "ready_for_signature", "signed", "issued"].includes(survey.status);
  const toggle = (id) => setSel((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const save = () => act(async () => {
    await apiRequest(`${sBase}/assign`, { method: "PUT", body: JSON.stringify({ user_ids: sel }) });
    toast.success(sel.length ? "Sopralluogo affidato" : "Assegnazione tolta");
    refresh();
  });
  return (
    <Card data-testid="safety-assign-card"><CardContent className="space-y-2 pt-4">
      <p className="text-sm font-medium">Chi fa il sopralluogo sul campo</p>
      {list.length === 0 && <p className="text-xs text-slate-500">Nessun auditor attivo: creane uno dalla voce «Auditor» del menu.</p>}
      <div className="flex flex-wrap gap-2">
        {list.map((u) => (
          <label key={u.id} className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
            <input type="checkbox" disabled={locked} checked={sel.includes(u.id)} onChange={() => toggle(u.id)} /> {u.name}
          </label>))}
      </div>
      {list.length > 0 && !locked && <Button size="sm" variant="outline" onClick={save}>Salva assegnazione</Button>}
    </CardContent></Card>
  );
}

const MISSING_LBL = { company: "dato azienda", meta: "dato anagrafico", task: "lavorazione", plan: "planimetria PDF" };

function SendCard({ data, done, busy, act, post, refresh, setTab }) {
  const { survey } = data;
  const p = survey.pipeline;
  const sent = survey.status === "human_review" || ["approved", "ready_for_signature", "signed", "issued"].includes(survey.status);
  const send = () => act(async () => {
    const r = await post("/send");
    toast.success(r.pipeline.outcome === "human_review" ? "Inviato alla revisione: pronto per il controllo umano" : "Inviato, ma l'app ha trovato blocchi da risolvere");
    refresh();
  });
  return (
    <Card data-testid="safety-send-card" className="border-blue-200"><CardContent className="space-y-3 pt-4">
      {!sent && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm">
            {done ? "La checklist è completa. Inviandola alla revisione, l'app elabora da sola i rischi, esegue le tre revisioni e prepara la bozza di ogni documento scelto."
              : "Completa le domande, poi potrai inviare il sopralluogo alla revisione."}
          </p>
          <Button disabled={busy || !done} onClick={send} data-testid="safety-send-button"><ClipboardCheck className="h-4 w-4" /> Invia alla revisione</Button>
        </div>)}
      {survey.submitted && <p className="text-xs text-slate-500">Inviato da {survey.submitted.by_name} il {new Date(survey.submitted.at).toLocaleString("it-IT")}.</p>}
      {survey.status === "human_review" && <p className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> In attesa del controllo umano: <button className="underline" onClick={() => setTab("umana")}>vai alla revisione</button>.</p>}
      {p && p.outcome === "blocked" && (
        <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertTitle>L'app ha trovato blocchi: non può ancora andare alla revisione</AlertTitle>
          <AlertDescription><ul className="list-disc pl-5">{p.why.map((w, i) => <li key={i}>{w}</li>)}</ul>
            <button className="mt-1 underline" onClick={() => setTab("revisioni")}>Vedi i rilievi</button> · risolvili, poi invia di nuovo.</AlertDescription></Alert>)}
      {p && p.documents?.length > 0 && (
        <div className="grid gap-2 md:grid-cols-2">
          {p.documents.map((d) => (
            <div key={d.kind} className="rounded border p-2 text-sm">
              <p className="flex items-center gap-2 font-medium">{d.ready ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />} {d.label}
                <Badge variant="outline">{d.generated ? `bozza: ${d.items} voci` : "non generata"}</Badge></p>
              {!d.ready && <p className="text-xs text-slate-600">Manca: {d.missing.map((m) => `${MISSING_LBL[m.type] || m.type} (${m.label})`).join("; ")}</p>}
            </div>))}
        </div>)}
    </CardContent></Card>
  );
}

function QuestionCard({ q, total, sBase, busy, act, refresh }) {
  const isMulti = q.answer_type === "multi_choice" || q.answer_type === "equipment_multi";
  const initial = () => (q.state === "answered" && q.value != null ? q.value : isMulti ? [] : "");
  const [value, setValue] = useState(initial);
  const [evidence, setEvidence] = useState(q.evidence_ids || []);
  useEffect(() => { setValue(initial()); setEvidence(q.evidence_ids || []); // eslint-disable-next-line
  }, [q.key, q.answer_type]);
  const send = (state, val) => act(async () => {
    await apiRequest(`${sBase}/answers`, { method: "POST", body: JSON.stringify({
      question_code: q.question_code, task_code: q.task_code, equipment_code: q.equipment_code, state,
      value: state === "answered" ? val : null, source: evidence.length ? "observed" : "declared", evidence_ids: evidence }) });
    refresh();
  });
  const upload = (req, file) => act(async () => {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", req.kind); fd.append("question_code", q.question_code);
    if (q.task_code) fd.append("task_code", q.task_code);
    if (q.equipment_code) fd.append("equipment_code", q.equipment_code);
    const r = await apiRequest(`${sBase}/evidence`, { method: "POST", body: fd });
    setEvidence((e) => [...e, r.id]); toast.success(`${req.label}: acquisita`);
  });
  const toggle = (v) => setValue((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  const isYN = q.answer_type === "yes_no_unknown";
  return (
    <Card data-testid="safety-question-card"><CardContent className="space-y-4 pt-6">
      <p className="text-xs text-slate-500">
        {q.task_code ? q.task_code : "Generale"}{q.equipment_code ? ` · ${eqName(q.equipment_code)}` : ""} · ancora {total} domande
      </p>
      <h3 className="text-xl font-semibold">{q.text}</h3>
      {q.help_text && <p className="text-sm text-slate-600">{q.help_text}</p>}
      {isYN && <div className="flex gap-2">
        <Button disabled={busy} onClick={() => send("yes")}>Sì</Button>
        <Button disabled={busy} variant="outline" onClick={() => send("no")}>No</Button>
      </div>}
      {q.answer_type === "single_choice" && <div className="space-y-1">
        {q.options.map((o) => <label key={o.value} className="flex items-center gap-2"><input type="radio" checked={value === o.value} onChange={() => setValue(o.value)} /> {o.label}</label>)}
      </div>}
      {(q.answer_type === "multi_choice" || q.answer_type === "equipment_multi") && <div className="space-y-1">
        {q.options.map((o) => <label key={o.value} className="flex items-center gap-2"><input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} /> {o.label}</label>)}
      </div>}
      {(q.answer_type === "number" || q.answer_type === "duration_min") && <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder={q.answer_type === "duration_min" ? "minuti" : ""} />}
      {q.answer_type === "text" && <Input value={value} onChange={(e) => setValue(e.target.value)} />}
      {q.evidence_requirements.map((r) => (
        <label key={r.code} className="flex cursor-pointer items-center gap-2 rounded border border-dashed p-2 text-sm">
          <Camera className="h-4 w-4" /> {r.label}
          <input type="file" accept="image/*,application/pdf" capture={r.kind === "photo" ? "environment" : undefined} className="hidden"
            onChange={(e) => e.target.files[0] && upload(r, e.target.files[0])} />
        </label>
      ))}
      {evidence.length > 0 && <p className="text-xs text-emerald-700">{evidence.length} allegati pronti</p>}
      <div className="flex flex-wrap gap-2">
        {!isYN && <Button disabled={busy || (Array.isArray(value) ? !value.length : value === "")} onClick={() => send("answered", q.answer_type.includes("number") || q.answer_type === "duration_min" ? Number(value) : value)}>Avanti</Button>}
        <Button disabled={busy} variant="ghost" onClick={() => send("unknown")}><HelpCircle className="h-4 w-4" /> Non so</Button>
        <Button disabled={busy} variant="ghost" onClick={() => send("not_verifiable")}>Non verificabile</Button>
      </div>
      {q.critical && <p className="text-xs text-amber-700">Domanda importante: se non sai, indicalo, verrà segnalato al revisore.</p>}
    </CardContent></Card>
  );
}

function Risks({ ev }) {
  return (
    <div className="space-y-3">
      {ev.risk_instances.length === 0 && <p className="text-sm text-slate-500">Nessun rischio individuato finora.</p>}
      {ev.risk_instances.map((r) => (
        <Card key={r.id}><CardContent className="space-y-1 pt-4">
          <div className="flex items-center justify-between"><span className="font-medium">{r.hazard_name}</span>
            {r.specialist_assessment_required && <Badge variant="outline" className="bg-purple-100 text-purple-800">Valutazione specialistica ({r.specialist_module})</Badge>}</div>
          <p className="text-xs text-slate-500">{r.task_code} · esposti: {r.exposed_roles.join(", ") || "da definire"}</p>
        </CardContent></Card>
      ))}
      {ev.findings.length > 0 && <Card><CardHeader><CardTitle className="text-base">Criticità emerse</CardTitle></CardHeader><CardContent className="space-y-2">
        {Array.from(new Map(ev.findings.map((f) => [f.message, f])).values()).map((f, i) => <div key={i} className="flex gap-2 text-sm"><SeverityBadge s={f.severity} /> {f.message}</div>)}
      </CardContent></Card>}
    </div>
  );
}

function ExpiryBadge({ d }) {
  if (d.status === "expired") return <Badge variant="outline" className="bg-red-100 text-red-800">scaduto il {d.expires_on}</Badge>;
  if (!d.expires_on) return null;
  const soon = d.days_to_expiry <= 30;
  return <Badge variant="outline" className={soon ? "bg-amber-100 text-amber-800" : ""}>scade il {d.expires_on}</Badge>;
}

function Units({ ev, sBase, units, act, refresh, locked }) {
  const types = Array.from(new Map(ev.equipment_instances.map((i) => [`${i.task_code}:${i.equipment_code}`, i])).values());
  const toText = (key) => (units[key] || []).map((u) => [u.id, u.label && u.label !== u.id ? u.label : "", u.next_check || ""].join(" | ")).join("\n");
  const [text, setText] = useState({});
  if (types.length === 0) return null;
  const save = (i, key) => act(async () => {
    const rows = (text[key] ?? toText(key)).split("\n").map((r) => r.trim()).filter(Boolean).map((r) => {
      const [id, label, next_check] = r.split("|").map((x) => x.trim());
      return { id, label: label || null, next_check: next_check || null };
    });
    await apiRequest(`${sBase}/equipment-units/${encodeURIComponent(i.task_code)}/${encodeURIComponent(i.equipment_code)}`, { method: "PUT", body: JSON.stringify({ units: rows }) });
    toast.success("Esemplari salvati");
    refresh();
  });
  return (
    <Card><CardHeader><CardTitle className="text-base">Esemplari delle attrezzature</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">Una riga per esemplare: matricola | nome | prossima verifica (AAAA-MM-GG). Ogni esemplare chiede i propri documenti e ha la propria scadenza.</p>
        {types.map((i) => {
          const key = `${i.task_code}:${i.equipment_code}`;
          return (
            <div key={key} className="space-y-1">
              <p className="text-sm font-medium">{i.equipment_code} <span className="text-xs text-slate-500">({i.task_code})</span></p>
              <Textarea rows={3} disabled={locked} placeholder="M1 | Martello Bosch | 2027-03-31" value={text[key] ?? toText(key)} onChange={(e) => setText({ ...text, [key]: e.target.value })} />
              {!locked && <Button size="sm" variant="outline" onClick={() => save(i, key)}>Salva esemplari</Button>}
            </div>
          );
        })}
      </CardContent></Card>
  );
}

function Docs({ ev, sBase, act, refresh, locked, units }) {
  const [dates, setDates] = useState({});
  const mark = (d, file) => {
    const expires_on = dates[d.key] || null;
    if (d.expiration_required && !expires_on) { toast.error("Indica prima la data di scadenza (o del prossimo controllo)"); return; }
    return act(async () => {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "document");
      const e = await apiRequest(`${sBase}/evidence`, { method: "POST", body: fd });
      await apiRequest(`${sBase}/documents/${encodeURIComponent(d.key)}`, { method: "PUT", body: JSON.stringify({ status: "received", evidence_id: e.id, expires_on }) });
      refresh();
    });
  };
  const st = ev.document_summary;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">{Object.entries(st).map(([k, v]) => `${v} ${k}`).join(" · ") || "Nessun documento richiesto"}</p>
      <Units ev={ev} sBase={sBase} units={units} act={act} refresh={refresh} locked={locked} />
      {ev.documents.map((d) => (
        <Card key={d.key}><CardContent className="flex items-center justify-between gap-3 pt-4">
          <div><p className="font-medium">{d.name}{d.subject ? ` (${d.subject})` : ""}</p>
            <p className="text-xs text-slate-500">{d.category}{d.blocking_if_missing ? " · bloccante" : ""}{d.expiration_required ? " · ha una scadenza" : ""}</p></div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{d.status}</Badge>
            <ExpiryBadge d={d} />
            {!locked && (d.status !== "received" || (d.days_to_expiry !== null && d.days_to_expiry <= 30)) && <>
              <Input type="date" aria-label="Scadenza del documento" className="h-8 w-36 text-xs" value={dates[d.key] || ""} onChange={(e) => setDates({ ...dates, [d.key]: e.target.value })} />
              <label className="cursor-pointer"><span className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs"><Upload className="h-3 w-3" /> {d.status === "received" ? "Rinnova" : "Carica"}</span>
                <input type="file" className="hidden" onChange={(e) => e.target.files[0] && mark(d, e.target.files[0])} /></label></>}
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}

function Reviews({ data, post, act, refresh, busy }) {
  const { survey, review_state: rs } = data;
  const steps = [["completeness", 1, "Completezza", "risk_analysis"], ["technical", 2, "Coerenza tecnica e normativa", "auto_review_1"], ["adversarial", 3, "Contraddittorio", "auto_review_2"]];
  return (
    <div className="space-y-3">
      {steps.map(([k, n, label, need]) => {
        const r = survey.reviews?.[String(n)];
        return (
          <Card key={k}><CardContent className="space-y-2 pt-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">Revisione {n}: {label}</span>
              <div className="flex items-center gap-2">
                {r && <Badge variant="outline" className={rs.fresh[n] ? "" : "bg-amber-100"}>{rs.fresh[n] ? `${r.checks_run} controlli` : "superata da modifiche"}</Badge>}
                <Button size="sm" disabled={busy || survey.status !== need} onClick={() => act(async () => { await post(`/review/${k}`); refresh(); })}><ClipboardCheck className="h-4 w-4" /> Esegui</Button>
              </div>
            </div>
            {r && rs.fresh[n] && r.findings.map((f, i) => <div key={i} className="flex gap-2 text-sm"><SeverityBadge s={f.severity} /> {f.message}</div>)}
          </CardContent></Card>
        );
      })}
      {survey.status === "risk_analysis" && <p className="text-xs text-slate-500">Esegui le revisioni nell'ordine 1, 2, 3.</p>}
      {survey.status === "survey_draft" && <p className="text-xs text-amber-700">Il sopralluogo è stato modificato: richiudilo dalla scheda Domande per riavviare le revisioni.</p>}
      {survey.status === "auto_review_3" && <Button disabled={busy} onClick={() => act(async () => { await post("/submit-human-review"); refresh(); })}>Invia al revisore umano</Button>}
    </div>
  );
}

function HumanGate({ data, post, act, refresh, user, exceptions }) {
  const { survey } = data;
  const [note, setNote] = useState("");
  const [reason, setReason] = useState({});
  const can = HUMAN_ROLES.includes(user?.role);
  const open = ["1", "2", "3"].flatMap((n) => (survey.reviews?.[n]?.findings || []));
  const waived = survey.waivers || {};
  if (survey.status !== "human_review" && survey.status !== "approved") return <p className="text-sm text-slate-500">Il documento non è ancora in revisione umana: completa prima le tre revisioni automatiche.</p>;
  return (
    <div className="space-y-4">
      {exceptions && <Card><CardContent className="pt-4 text-sm">
        <p className="font-medium">{exceptions.checks_run} controlli eseguiti</p>
        <p>{Object.entries(exceptions.counts).filter(([, v]) => v).map(([k, v]) => `${v} ${k}`).join(" · ") || "Nessuna eccezione aperta"}</p>
      </CardContent></Card>}
      {open.map((f, i) => {
        const key = f.key || `${f.code}|${f.task_code || "-"}`;
        return (
          <Card key={i}><CardContent className="space-y-2 pt-4 text-sm">
            <div className="flex gap-2"><SeverityBadge s={f.severity} /> {f.message}{waived[key] && <Badge variant="outline">accettato</Badge>}</div>
            {waived[key] && <p className="text-xs text-slate-500">Motivo: {waived[key].reason}</p>}
            {survey.status === "human_review" && can && !waived[key] && f.severity !== "blocker" && (
              <div className="flex gap-2">
                <Input placeholder="Motivazione per accettare l'eccezione" value={reason[key] || ""} onChange={(e) => setReason({ ...reason, [key]: e.target.value })} />
                <Button size="sm" variant="outline" onClick={() => act(async () => { await post("/waive", { key: f.key, code: f.code, task_code: f.task_code, reason: reason[key] || "" }); refresh(); })}>Accetta</Button>
              </div>)}
          </CardContent></Card>
        );
      })}
      {survey.status === "human_review" && (can
        ? <div className="space-y-2">
            <Textarea placeholder="Note del revisore" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-2">
              <Button onClick={() => act(async () => { await post("/approve", { note }); refresh(); })}><ShieldCheck className="h-4 w-4" /> Approva</Button>
              <Button variant="outline" onClick={() => act(async () => { await post("/request-changes", { note }); refresh(); })}><AlertTriangle className="h-4 w-4" /> Richiedi modifiche</Button>
            </div>
          </div>
        : <Alert><AlertDescription>Solo SUPER_ADMIN, CONSULENTE e RSPP possono approvare.</AlertDescription></Alert>)}
      {survey.status === "approved" && <Alert><AlertDescription>Approvato da {survey.approval?.by_name}. Il documento resta in bozza fino a firma ed emissione.</AlertDescription></Alert>}
    </div>
  );
}

function Draft({ sBase, survey }) {
  const kinds = survey.document_kinds || [survey.document_kind];
  const [kind, setKind] = useState(kinds[0]);
  const [doc, setDoc] = useState(null);
  useEffect(() => { setDoc(null); apiRequest(`${sBase}/document?kind=${kind}`).then(setDoc).catch((e) => toast.error(e.message)); }, [sBase, kind, survey.updated_at]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">{kinds.map((k) => <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}>{k.toUpperCase()}</Button>)}</div>
      {!doc ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <Card className="relative overflow-hidden"><CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> {doc.title} <Badge variant="outline">{doc.status}</Badge></CardTitle></CardHeader>
          {doc.watermark && <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 flex select-none items-center justify-center text-[8rem] font-extrabold tracking-widest text-slate-900/10 print:text-slate-900/20" style={{ transform: "rotate(-25deg)" }}>{doc.watermark}</div>}
          <CardContent className="space-y-4 text-justify">
            <p className="text-sm italic">{doc.disclaimer}</p>
            {doc.sections.map((sec) => sec.items.length > 0 && (
              <div key={sec.title}><h4 className="font-semibold">{sec.title}</h4>
                <ul className="list-disc pl-5 text-sm">{sec.items.map((i, k) => <li key={k}>{i.text}</li>)}</ul></div>
            ))}
          </CardContent></Card>)}
    </div>
  );
}
